<template>
  <Popup
    ref="popup"
    @close="$emit('close')"
  >
    <template #icon>
      <svg
    viewBox="0 0 24 24"
    fill="none"
 class="w-5 h-5 text-gray-600">
    <path
        d="M8 5.00005C7.01165 5.00082 6.49359 5.01338 6.09202 5.21799C5.71569 5.40973 5.40973 5.71569 5.21799 6.09202C5 6.51984 5 7.07989 5 8.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.07989 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V8.2C19 7.07989 19 6.51984 18.782 6.09202C18.5903 5.71569 18.2843 5.40973 17.908 5.21799C17.5064 5.01338 16.9884 5.00082 16 5.00005M8 5.00005V7H16V5.00005M8 5.00005V4.70711C8 4.25435 8.17986 3.82014 8.5 3.5C8.82014 3.17986 9.25435 3 9.70711 3H14.2929C14.7456 3 15.1799 3.17986 15.5 3.5C15.8201 3.82014 16 4.25435 16 4.70711V5.00005"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
    />
</svg>
    </template>

    <template #header>
      {{ $props.plantCategory ? $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.title.update') : $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.title.add') }}
    </template>

    <template #body>
      <form
        class="space-y-6"
        @submit.prevent="savePlantCategory"
      >
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.label.name') }}
          </label>

          <AppInput
            v-model="plantCategoryForm.form.name"
            name="name"
            type="text"
            :error="plantCategoryForm.getErrors('name')"
            data-test="update-name"
            class="form-input"
            placeholder="Enter plant category name"
          />
        </div>
      </form>
    </template>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <button
          v-if="$props.plantCategory"
          type="button"
          class="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
          @click="deletePlantCategory"
        >
          {{ $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.button.delete') }}
        </button>

        <div class="flex items-center space-x-3 ml-auto">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            @click="popup.close(); plantCategoryForm.resetData()"
          >
            {{ $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.button.cancel') }}
          </button>

          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            data-test="save-order"
            @click="savePlantCategory"
          >
            {{ $props.plantCategory ? $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.button.update') : $t('ProjectSettings.SelfPerformTab.PlantCategoryModal.button.create') }}
          </button>
        </div>
      </div>
    </template>
  </Popup>
</template>

<script setup lang="ts">
import Popup from '@/components/Popup.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import PlantCategory from '@/models/PlantCategory';
import { usePlantCategoriesStore } from '@/store/plantCategories';
import Swal from 'sweetalert2';
import { useTemplateRef, watch } from 'vue';

const props = defineProps<{
  plantCategory?: PlantCategory;
  projectId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const plantCategoriesStore = usePlantCategoriesStore();
const popup = useTemplateRef('popup');

const plantCategoryForm = useFormData({
  name: '',
});

watch(() => props.plantCategory, (newValue) => {
  if(newValue) {
    plantCategoryForm.setData(newValue);
  }
}, { immediate: true });

const updatePlantCategoryAction = useStoreApiAction(plantCategoriesStore.updatePlantCategory);
const createPlantCategoryAction = useStoreApiAction(plantCategoriesStore.createPlantCategory);

const savePlantCategory = () => {
  plantCategoryForm.resetErrors();

  if(props.plantCategory) {
    updatePlantCategoryAction.request(props.plantCategory.id, plantCategoryForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        text: t('ProjectSettings.SelfPerformTab.PlantCategoryModal.message.updated'),
      }).then(() => {
        popup.value.close();
        plantCategoryForm.resetData();
      });
    }).catch((error) => {
      plantCategoryForm.setErrors(error.data);
    });
  } else {
    createPlantCategoryAction.request(props.projectId, plantCategoryForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        text: t('ProjectSettings.SelfPerformTab.PlantCategoryModal.message.created'),
      }).then(() => {
        popup.value.close();
        plantCategoryForm.resetData();
      });
    }).catch((error) => {
      plantCategoryForm.setErrors(error.data);
    });
  }
};

const deletePlantCategory = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.SelfPerformTab.PlantCategoryModal.confirm.deleteTitle'),
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      plantCategoriesStore.deletePlantCategory(props.plantCategory.id).then(() => {
        popup.value.close();
      }).catch(() => {
        Swal.fire({
          icon: 'error',
          title: t('ProjectSettings.SelfPerformTab.PlantCategoryModal.message.deleteFailed'),
        });
      });
    }
  });
};
</script>

<style scoped></style>
